import React, { useEffect, useState } from 'react';
import { cropConcernCircle, circleFitImage, findBestBboxForConcern } from '../../utils/concernCropUtils';
import {
  getConcernStatusChip,
  getDetailedSeverityLabel,
  buildDetailedConcernBlurb,
} from '../../utils/reportSeverity';
import {
  canonicalSkinConditionName,
  isSkinConditionName,
} from '../../utils/skinResultsUtils';
import { fetchSkinConcernMask, conditionKeyFromDisplay } from '../../utils/lazySkinMask';
import { getSkinConditionKeyCandidates } from '../../utils/maskUtils';

function resolveExistingMaskUrl(skinConditions, displayName, apiBaseUrl) {
  const canonical = canonicalSkinConditionName(displayName);
  if (!canonical || !skinConditions || typeof skinConditions !== 'object') return null;
  const altKeys = [
    conditionKeyFromDisplay(canonical),
    ...getSkinConditionKeyCandidates(canonical),
    canonical,
    canonical.toLowerCase(),
    String(displayName || '').toLowerCase().replace(/\s+/g, '_'),
  ].filter(Boolean);
  let maskUrl = null;
  for (const k of Array.from(new Set(altKeys))) {
    const entry = skinConditions[k];
    if (entry && typeof entry === 'object' && entry.mask) {
      maskUrl = entry.mask;
      break;
    }
    if (typeof entry === 'string' && entry) {
      maskUrl = entry;
      break;
    }
  }
  if (typeof maskUrl !== 'string' || !maskUrl) return null;
  if (maskUrl.startsWith('/') && apiBaseUrl) {
    return `${String(apiBaseUrl).replace(/\/$/, '')}${maskUrl}`;
  }
  return maskUrl;
}

function lookupDescription(descriptions, item, key) {
  if (!descriptions || typeof descriptions !== 'object') return '';
  const candidates = [
    item?.class_name,
    item?.label,
    key,
    canonicalSkinConditionName(item?.class_name),
    canonicalSkinConditionName(item?.label),
  ].filter(Boolean);
  for (const c of candidates) {
    const val = descriptions[c] || descriptions[String(c).toLowerCase()];
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (val && typeof val === 'object') {
      const text = val.description || val.text || val.summary || '';
      if (String(text).trim()) return String(text).trim();
    }
  }
  return '';
}

/**
 * Reference layout: circular crop ΓåÆ title ΓåÆ severity wording ΓåÆ blurb ΓåÆ status chip.
 * Attribute concerns (Hydration/Oxygen/Smoothness/Dullness/Uneven) use mask images when available.
 */
export default function SkinDetailedCrops({
  items = [],
  imageSrc,
  detections = [],
  descriptions = {},
  skinConditions = null,
  sessionId = null,
  token = null,
  apiBaseUrl = '',
  maxItems = 6,
}) {
  const [crops, setCrops] = useState({});

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!items.length) {
        setCrops({});
        return;
      }
      const next = {};
      const slice = items.slice(0, maxItems);
      await Promise.all(slice.map(async (item) => {
        const key = item.class_name || item.key;
        const label = item.label || item.class_name;

        // Attribute concerns ΓåÆ mask image inside the circle
        if (isSkinConditionName(key) || isSkinConditionName(label)) {
          const canonical = canonicalSkinConditionName(key) || canonicalSkinConditionName(label);
          let maskUrl = resolveExistingMaskUrl(skinConditions, canonical || label, apiBaseUrl);
          if (!maskUrl && sessionId && canonical) {
            try {
              maskUrl = await fetchSkinConcernMask({
                sessionId,
                displayName: canonical,
                appSessionToken: token || null,
              });
            } catch (_) {
              maskUrl = null;
            }
          }
          if (maskUrl) {
            const circled = await circleFitImage(maskUrl, 140);
            if (circled) {
              next[key] = circled;
              return;
            }
            next[key] = maskUrl;
            return;
          }
        }

        // Detection concerns ΓåÆ face crop from bbox
        if (!imageSrc) return;
        const bbox = findBestBboxForConcern(detections, key)
          || findBestBboxForConcern(detections, label);
        const url = await cropConcernCircle(imageSrc, bbox, 140);
        if (url) next[key] = url;
        else if (imageSrc) {
          // Fallback: centered face crop so the circle is never empty
          const fitted = await circleFitImage(imageSrc, 140);
          if (fitted) next[key] = fitted;
        }
      }));
      if (!cancelled) setCrops(next);
    };
    void run();
    return () => { cancelled = true; };
  }, [imageSrc, items, detections, skinConditions, sessionId, token, apiBaseUrl, maxItems]);

  if (!items.length) return null;

  return (
    <div className="skin-report-crops" aria-label="Detailed skin analysis">
      {items.slice(0, maxItems).map((item, idx) => {
        const key = item.class_name || item.key || `crop-${idx}`;
        const title = item.label || item.class_name;
        const score = Number(item.score) || 0;
        const severity = getDetailedSeverityLabel(score, title);
        const chip = getConcernStatusChip(score);
        const blurb = buildDetailedConcernBlurb(item, lookupDescription(descriptions, item, key));
        return (
          <article key={key} className={`skin-report-crop-card skin-report-crop-card--${chip.tone}`}>
            <div className="skin-report-crop-ring">
              {crops[key] ? (
                <img src={crops[key]} alt="" className="skin-report-crop-img" />
              ) : (
                <div className="skin-report-crop-placeholder" aria-hidden="true" />
              )}
            </div>
            <h4 className="skin-report-crop-title">{title}</h4>
            <p className={`skin-report-crop-status skin-report-crop-status--${severity.tone}`}>
              {severity.label}
            </p>
            {blurb ? <p className="skin-report-crop-blurb">{blurb}</p> : null}
            <span className={`skin-report-crop-dot skin-report-crop-dot--${chip.tone}`}>
              <span className="skin-report-crop-dot-mark" aria-hidden="true" />
              {chip.label}
            </span>
          </article>
        );
      })}
    </div>
  );
}
